import React, { useState, useEffect } from 'react';
import { orgApi, adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Building2, Plus, Edit, Trash2, Users, Search,
  Phone, MapPin, ChevronRight, UserPlus, Shield
} from 'lucide-react';
import { toast } from 'sonner';

export default function OrganizationManagementPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', address: '', phone: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'physician', organization_id: '' });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadOrgs(); }, []);

  const loadOrgs = async () => {
    setLoading(true);
    try {
      const res = await orgApi.list();
      setOrgs(res.data || []);
      if (res.data?.length > 0 && !selectedOrg) {
        selectOrg(res.data[0]);
      }
    } catch (err) {
      console.error('Failed to load orgs:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectOrg = async (org) => {
    setSelectedOrg(org);
    try {
      const res = await orgApi.getUsers(org.id);
      setOrgUsers(res.data.users || []);
    } catch (err) {
      setOrgUsers([]);
    }
  };

  const handleCreateOrg = async () => {
    if (!orgForm.name) { toast.error('Organization name required'); return; }
    try {
      await orgApi.create(orgForm);
      toast.success('Organization created');
      setShowCreateOrg(false);
      setOrgForm({ name: '', address: '', phone: '' });
      loadOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleEditOrg = async () => {
    if (!selectedOrg) return;
    try {
      await orgApi.update(selectedOrg.id, orgForm);
      toast.success('Organization updated');
      setShowEditOrg(false);
      loadOrgs();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? This cannot be undone.`)) return;
    try {
      await orgApi.remove(org.id);
      toast.success('Organization deleted');
      if (selectedOrg?.id === org.id) setSelectedOrg(null);
      loadOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('All fields are required'); return;
    }
    try {
      await adminApi.createUser({ ...userForm, organization_id: selectedOrg.id });
      toast.success('User created');
      setShowCreateUser(false);
      setUserForm({ name: '', email: '', password: '', role: 'physician', organization_id: '' });
      selectOrg(selectedOrg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditOrg = (org) => {
    setOrgForm({ name: org.name, address: org.address || '', phone: org.phone || '' });
    setShowEditOrg(true);
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-[#991B1B]/10 text-[#991B1B]',
      practice_admin: 'bg-[#0A2342]/10 text-[#0A2342]',
      physician: 'bg-[#0E7490]/10 text-[#0E7490]',
      nurse: 'bg-[#1A56DB]/10 text-[#1A56DB]',
      care_coordinator: 'bg-[#0E7490]/10 text-[#0E7490]',
      aide: 'bg-[#9A3412]/10 text-[#9A3412]',
      billing_specialist: 'bg-[#92400E]/10 text-[#92400E]',
      patient: 'bg-[#166534]/10 text-[#166534]'
    };
    return colors[role] || 'bg-[#F1F5F9] text-[#64748B]';
  };

  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="org-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Organizations
          </h1>
          <p className="text-[#64748B] mt-1">{orgs.length} organizations on platform</p>
        </div>
        <Button className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => { setOrgForm({ name: '', address: '', phone: '' }); setShowCreateOrg(true); }} data-testid="create-org-btn">
          <Plus className="w-4 h-4 mr-2" /> New Organization
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Org List */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input placeholder="Search organizations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="org-search" />
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
          ) : filteredOrgs.length > 0 ? (
            filteredOrgs.map(org => (
              <Card key={org.id} className={`careiq-card cursor-pointer hover:shadow-md transition-all ${selectedOrg?.id === org.id ? 'border-[#0E7490] border-2' : ''}`} onClick={() => selectOrg(org)} data-testid={`org-card-${org.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: org.primary_color || '#0A2342' }}>
                        {org.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-[#0A2342]">{org.name}</div>
                        {org.address && <div className="text-xs text-[#64748B] flex items-center gap-1"><MapPin className="w-3 h-3" />{org.address}</div>}
                        {org.phone && <div className="text-xs text-[#64748B] flex items-center gap-1"><Phone className="w-3 h-3" />{org.phone}</div>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              <Building2 className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
              <p className="text-sm">No organizations found</p>
            </div>
          )}
        </div>

        {/* Org Detail + Users */}
        <div className="lg:col-span-2 space-y-4">
          {selectedOrg ? (
            <>
              <Card className="careiq-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-[#0A2342]">{selectedOrg.name}</h2>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#64748B]">
                        {selectedOrg.address && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{selectedOrg.address}</span>}
                        {selectedOrg.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{selectedOrg.phone}</span>}
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-2">Created: {new Date(selectedOrg.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditOrg(selectedOrg)} data-testid="edit-org-btn">
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-[#991B1B] hover:bg-[#991B1B]/10" onClick={() => handleDeleteOrg(selectedOrg)} data-testid="delete-org-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Users in this Org */}
              <Card className="careiq-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#0E7490]" /> Users ({orgUsers.length})
                  </CardTitle>
                  <Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => setShowCreateUser(true)} data-testid="add-user-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Add User
                  </Button>
                </CardHeader>
                <CardContent>
                  {orgUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                            <th className="text-left p-3 text-[#64748B] font-medium">Name</th>
                            <th className="text-left p-3 text-[#64748B] font-medium">Email</th>
                            <th className="text-left p-3 text-[#64748B] font-medium">Role</th>
                            <th className="text-left p-3 text-[#64748B] font-medium">MFA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orgUsers.map(u => (
                            <tr key={u.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`user-row-${u.id}`}>
                              <td className="p-3 font-medium text-[#0A2342]">{u.name}</td>
                              <td className="p-3 text-[#64748B]">{u.email}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                                  {u.role.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="p-3">
                                {u.mfa_enabled ? <Shield className="w-4 h-4 text-[#166534]" /> : <span className="text-[#94A3B8]">-</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#64748B]">
                      <Users className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                      <p className="text-sm">No users in this organization</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-[#64748B]">
              <div className="text-center">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
                <p>Select an organization to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="sm:max-w-md" aria-describedby="create-org-desc">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <p id="create-org-desc" className="text-sm text-[#64748B]">Add a new healthcare organization to the platform</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Organization Name *</Label>
              <Input value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} placeholder="e.g., Metro Health System" className="mt-1" data-testid="org-name-input" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={orgForm.address} onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })} placeholder="123 Medical Dr, City, ST 12345" className="mt-1" data-testid="org-address-input" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={orgForm.phone} onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1" data-testid="org-phone-input" />
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleCreateOrg} data-testid="submit-create-org">
              <Building2 className="w-4 h-4 mr-2" /> Create Organization
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditOrg} onOpenChange={setShowEditOrg}>
        <DialogContent className="sm:max-w-md" aria-describedby="edit-org-desc">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <p id="edit-org-desc" className="text-sm text-[#64748B]">Update organization details</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Organization Name</Label>
              <Input value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={orgForm.address} onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={orgForm.phone} onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })} className="mt-1" />
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleEditOrg} data-testid="submit-edit-org">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="sm:max-w-md" aria-describedby="create-user-desc">
          <DialogHeader>
            <DialogTitle>Add User to {selectedOrg?.name}</DialogTitle>
            <p id="create-user-desc" className="text-sm text-[#64748B]">Create a new user under this organization</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Full Name *</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Dr. Jane Smith" className="mt-1" data-testid="user-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="jane@hospital.com" className="mt-1" data-testid="user-email-input" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Min 12 characters" className="mt-1" data-testid="user-password-input" />
            </div>
            <div>
              <Label>Role</Label>
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="user-role-select">
                <option value="practice_admin">Practice Admin</option>
                <option value="physician">Physician</option>
                <option value="nurse">Nurse</option>
                <option value="care_coordinator">Care Coordinator</option>
                <option value="aide">Aide (Home Care)</option>
                <option value="billing_specialist">Billing Specialist</option>
                <option value="patient">Patient</option>
              </select>
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleCreateUser} data-testid="submit-create-user">
              <UserPlus className="w-4 h-4 mr-2" /> Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
